import { Link } from "react-router-dom";
import { Download, History, LogOut, Maximize, Minimize, Moon, MousePointerClick, Settings, Sun, Volume2, VolumeX } from "lucide-react";
import { fmtDate } from "../lib/constants";
import { HINT, HowWeCountButton } from "./HowWeCount";
import { HygienePill } from "./HygienePill";

const pad = (n) => String(n).padStart(2, "0");
export const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const IconButton = ({ testId, onClick, title, active, children, to, className = "" }) => {
  const cls = `inline-flex items-center justify-center w-9 h-9 tv:w-[43px] tv:h-[43px] rounded-full border transition-colors shrink-0 ${
    active ? "border-brand/40 bg-brand/5 text-brand" : "border-panel-border bg-panel text-slate-500 hover:text-ink hover:border-slate-400"
  } ${className}`;
  if (to) return <Link to={to} data-testid={testId} title={title} className={cls}>{children}</Link>;
  return <button type="button" data-testid={testId} onClick={onClick} title={title} aria-pressed={active} className={cls}>{children}</button>;
};

const pill = "inline-flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 py-1.5 sm:px-3 sm:py-2 tv:px-[16px] tv:py-[10px] text-xs tv:text-[16px] font-semibold shadow-sm transition-colors shrink-0";

const StatusBadge = ({ status, lastSuccess, countdown, className = "" }) => {
  const badge =
    status === "live"
      ? { testId: "status-badge-live", dot: "bg-live live-dot", word: "LIVE", wordCls: "text-live", detail: `refreshed ${hhmm(lastSuccess)}`, prefix: "HubSpot · ", suffix: countdown }
      : status === "delayed"
        ? { testId: "status-badge-delayed", dot: "bg-delayed", word: "DELAYED", wordCls: "text-delayed", detail: `last refresh ${hhmm(lastSuccess)}` }
        : { testId: "status-badge-waiting", dot: "bg-slate-400 live-dot", word: "CONNECTING", wordCls: "text-slate-500", detail: "waiting for first HubSpot refresh" };
  return (
    <div data-testid={badge.testId} title={HINT.live} className={`flex items-center gap-2 tv:gap-[10px] text-xs sm:text-sm tv:text-[17px] rounded-full px-3 py-1 tv:px-[15px] tv:py-[6px] min-w-0 ${status === "delayed" ? "bg-delayed/10" : ""} ${className}`}>
      <span className={`w-2.5 h-2.5 tv:w-[12px] tv:h-[12px] rounded-full shrink-0 ${badge.dot}`} />
      <span className={`font-extrabold tracking-wide ${badge.wordCls}`}>{badge.word}</span>
      <span className="num text-slate-500 whitespace-nowrap truncate min-w-0">
        {badge.prefix ? <span className="hidden sm:inline">{badge.prefix}</span> : null}
        {badge.detail}
        {badge.suffix ? <span className="hidden sm:inline">{badge.suffix}</span> : null}
      </span>
    </div>
  );
};

export const TopBar = ({ status, lastSuccess, nextRefresh, now, isFullscreen, onFullscreen, soundOn, onSoundToggle, historyMode, onHistoryToggle, countFrom, showAdminLink = true, user, onSignOut, onHelp, onDetails, onInstall, hygiene, onHygiene, theme }) => {
  const secs = nextRefresh ? Math.round((nextRefresh - now) / 1000) : null;
  const countdown = secs === null ? "" : secs > 0 ? ` · next in ${Math.floor(secs / 60)}:${pad(secs % 60)}` : " · refreshing…";
  const icon = "w-4 h-4 tv:w-[21px] tv:h-[21px]";
  const historyOn = historyMode === "all";

  return (
    <header className="flex flex-wrap items-center gap-x-3 gap-y-2 tv:gap-[14px] px-1" data-testid="topbar">
      <h1 data-testid="topbar-title" className="flex items-center gap-3 tv:gap-[15px] min-w-0">
        <span className="inline-flex items-center bg-coal rounded-lg px-2.5 py-1.5 tv:px-[14px] tv:py-[9px] shrink-0">
          <img src="/fora-logo.png" alt="FORA" data-testid="fora-logo" className="h-4 sm:h-5 tv:h-[24px] w-auto select-none" draggable={false} />
        </span>
        <span className="font-bold tracking-tight text-xl sm:text-2xl tv:text-[29px] leading-none text-ink truncate">Clients Live</span>
      </h1>
      <div className="flex-1" />
      <StatusBadge status={status} lastSuccess={lastSuccess} countdown={countdown} className="hidden md:flex" />
      <div data-testid="live-clock" className="num font-bold text-xl sm:text-2xl tv:text-[30px] tabular-nums text-ink">
        {hhmm(now)}
        <span className="text-slate-400">:{pad(now.getSeconds())}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 tv:gap-[9px] basis-full md:basis-auto min-w-0" data-testid="topbar-controls">
        <StatusBadge status={status} lastSuccess={lastSuccess} countdown={countdown} className="md:hidden basis-full px-0" />
        {onHistoryToggle && (
          <button
            type="button"
            data-testid="history-toggle-button"
            onClick={onHistoryToggle}
            aria-pressed={historyOn}
            title={historyOn ? `Showing all clients incl. history before ${fmtDate(countFrom)} – click to show only this month (since ${fmtDate(countFrom)})` : `Showing this month's clients – since ${fmtDate(countFrom)}; the counter restarts on the 1st of every month. Click to include history before that date`}
            className={`${pill} ${historyOn ? "border-brand/40 bg-brand/5 text-brand" : "border-panel-border bg-panel text-slate-700 hover:border-slate-400"}`}
          >
            <History className={icon} />
            <span data-testid="history-toggle-label" className="whitespace-nowrap">
              {historyOn ? "History: on" : <><span className="hidden sm:inline">Since </span>{fmtDate(countFrom) || "…"}</>}
            </span>
          </button>
        )}
        {onDetails && (
          <button
            type="button"
            data-testid="topbar-details-button"
            onClick={onDetails}
            title="Day-by-day details: venues, products, countries, partners"
            className={`${pill} border-brand/40 bg-brand/5 text-brand hover:bg-brand hover:text-white`}
          >
            <MousePointerClick className={icon} />
            <span className="whitespace-nowrap">Details</span>
          </button>
        )}
        {onHygiene && <HygienePill summary={hygiene} onClick={onHygiene} />}
        {onHelp && (
          <HowWeCountButton onClick={onHelp} testId="how-we-count-button" size={icon} className="hidden sm:inline-flex w-9 h-9 tv:w-[43px] tv:h-[43px] border border-panel-border bg-panel text-slate-500 hover:text-brand hover:border-slate-400 shrink-0" />
        )}
        {onSoundToggle && (
          <IconButton testId="sound-toggle-button" onClick={onSoundToggle} active={soundOn} title={soundOn ? "Chime on – click to mute" : "Chime muted – click to enable"}>
            {soundOn ? <Volume2 className={icon} /> : <VolumeX className={icon} />}
          </IconButton>
        )}
        {theme && (
          <IconButton testId="theme-toggle-button" onClick={theme.toggle} active={theme.dark} title={theme.dark ? "Dark theme on – switch to light (this device only)" : "Switch to the dark theme (this device only)"}>
            {theme.dark ? <Sun className={icon} /> : <Moon className={icon} />}
          </IconButton>
        )}
        {showAdminLink && (
          <IconButton testId="admin-link" to="/admin" title="Admin" className="hidden sm:inline-flex">
            <Settings className={icon} />
          </IconButton>
        )}
        {onInstall && (
          <IconButton testId="install-app-button" onClick={onInstall} title="Install FORA Live on this device (home screen, full screen)" active>
            <Download className={icon} />
          </IconButton>
        )}
        {onSignOut && (
          <IconButton testId="sign-out-button" onClick={onSignOut} title={user?.method === "display" ? `Display link "${user.display}" – disconnect this screen` : user?.email ? `Signed in as ${user.email} – sign out` : "Sign out"}>
            <LogOut className={icon} />
          </IconButton>
        )}
        <button
          type="button"
          data-testid="fullscreen-toggle-button"
          onClick={onFullscreen}
          title={isFullscreen ? "Exit full screen" : "Full screen"}
          className={`${pill} hidden sm:inline-flex border-panel-border bg-panel text-slate-700 hover:border-slate-400`}
        >
          {isFullscreen ? <Minimize className={icon} /> : <Maximize className={icon} />}
          <span className="hidden sm:inline tv:hidden">{isFullscreen ? "Exit" : "Full screen"}</span>
        </button>
      </div>
    </header>
  );
};
